import { useMemo } from "react";
import { motion } from "framer-motion";
import { geoEquirectangular, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import { useLang } from "../../i18n/LanguageContext";
import worldData from "../../lib/world-110m.json";

const W = 980;
const H = 500;
const FIJI = [178.065, -17.713];

// Key investor markets we connect to Fiji, with representative [lon, lat].
const MARKETS = [
  { name: "United States", coord: [-98, 39] },
  { name: "Canada", coord: [-106, 56] },
  { name: "United Kingdom", coord: [-1.5, 52.5] },
  { name: "United Arab Emirates", coord: [54, 24] },
  { name: "India", coord: [78, 22] },
  { name: "Singapore", coord: [103.8, 1.35] },
  { name: "China", coord: [104, 35] },
  { name: "Japan", coord: [138, 37] },
  { name: "Australia", coord: [134, -25] },
  { name: "New Zealand", coord: [172, -41] },
];

// Upward-bowing quadratic arc between two projected points.
const arcPath = ([ax, ay], [bx, by]) => {
  const mx = (ax + bx) / 2;
  const dist = Math.hypot(bx - ax, by - ay);
  const cy = Math.min(ay, by) - dist * 0.28;
  return `M ${ax} ${ay} Q ${mx} ${cy} ${bx} ${by}`;
};

export const WorldConnect = () => {
  const { t } = useLang();
  const w = t.world;

  const { landPath, fiji, points } = useMemo(() => {
    const projection = geoEquirectangular().rotate([-180, 0]).fitSize([W, H], { type: "Sphere" });
    const path = geoPath(projection);
    const land = feature(worldData, worldData.objects.land);
    const fijiPt = projection(FIJI);
    const pts = MARKETS.map((m) => ({ ...m, xy: projection(m.coord) })).filter((m) => m.xy);
    return { landPath: path(land), fiji: fijiPt, points: pts };
  }, []);

  return (
    <section id="world" className="relative py-24 sm:py-32 bg-[var(--page)] text-[var(--ink)] border-t border-[var(--hairline)] overflow-hidden" data-testid="world-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="max-w-2xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[var(--ink)]" />
            <span className="text-[11px] sm:text-xs uppercase tracking-[0.25em] text-[var(--muted-ink)] font-semibold" data-testid="world-overline">{w.overline}</span>
          </div>
          <h2 className="font-display font-semibold tracking-tight leading-[1.03] text-3xl sm:text-4xl lg:text-5xl" data-testid="world-title">{w.title}</h2>
          <p className="mt-6 text-base sm:text-lg text-[var(--muted-ink)] leading-relaxed">{w.sub}</p>
        </div>

        <div className="mt-12 sm:mt-16 relative" dir="ltr">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="w-full h-auto"
            role="img"
            aria-label="World map showing international investor markets connecting to Fiji"
            data-testid="world-map"
          >
            {/* Landmasses */}
            <path d={landPath} fill="var(--ink)" fillOpacity="0.07" stroke="var(--ink)" strokeOpacity="0.18" strokeWidth="0.5" />

            {/* Connection arcs */}
            {points.map((p, i) => (
              <motion.path
                key={p.name}
                d={arcPath(p.xy, fiji)}
                fill="none"
                stroke="var(--ink)"
                strokeOpacity="0.35"
                strokeWidth="1.1"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.4, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              />
            ))}

            {/* Origin markers + labels */}
            {points.map((p, i) => {
              const nearRight = p.xy[0] > W - 90;
              const nearLeft = p.xy[0] < 90;
              const anchor = nearRight ? "end" : nearLeft ? "start" : "middle";
              const lx = nearRight ? p.xy[0] - 6 : nearLeft ? p.xy[0] + 6 : p.xy[0];
              return (
              <motion.g
                key={`m-${p.name}`}
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.2 + i * 0.12 }}
                style={{ transformOrigin: `${p.xy[0]}px ${p.xy[1]}px` }}
              >
                <circle cx={p.xy[0]} cy={p.xy[1]} r="3" fill="var(--ink)" />
                <text
                  x={lx}
                  y={p.xy[1] - 8}
                  textAnchor={anchor}
                  className="fill-[var(--ink)]"
                  style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.02em", opacity: 0.6 }}
                >
                  {p.name}
                </text>
              </motion.g>
              );
            })}

            {/* Fiji hub with pulsing rings */}
            <g>
              <motion.circle
                cx={fiji[0]} cy={fiji[1]} r="6" fill="none" stroke="var(--ink)" strokeWidth="1.2"
                initial={{ opacity: 0.5, scale: 1 }}
                animate={{ opacity: 0, scale: 3.2 }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
                style={{ transformOrigin: `${fiji[0]}px ${fiji[1]}px` }}
              />
              <circle cx={fiji[0]} cy={fiji[1]} r="5.5" fill="var(--ink)" />
              <circle cx={fiji[0]} cy={fiji[1]} r="10" fill="none" stroke="var(--ink)" strokeOpacity="0.35" strokeWidth="1" />
              <text
                x={fiji[0]} y={fiji[1] + 24} textAnchor="middle"
                className="fill-[var(--ink)]"
                style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.04em" }}
                data-testid="world-hub-label"
              >
                {w.hub}
              </text>
            </g>
          </svg>
        </div>

        {/* Accessible / crawlable market list */}
        <div className="mt-10 border-t border-[var(--hairline)] pt-6">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[var(--muted-ink)] font-semibold">{w.markets}</p>
          <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2" data-testid="world-market-list">
            {MARKETS.map((m) => (
              <li key={m.name} className="text-sm text-[var(--ink)]/80 font-medium">{m.name}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};
